/**
 * @module AlgoliaCncs
 * @description Initializes and manages Algolia product search grid for CNCs.
 *
 * Uses the base product search grid component factory for consistency across product types.
 * This is specifically for product search grids, not general site search.
 */

import { createProductSearchGrid } from './BaseProductSearchGrid.js';
import { createProductConfig } from '@js/algolia/createProductConfig.js';
import { cncsConfig } from '@js/config/algolia.js';

/**
 * Get array of specs to display for a CNC
 * @param {Object} result - The search result
 * @param {Object} translations - Translation labels for spec names
 * @returns {Array} Array of spec objects with icon, label, value, and unit
 */
function getSpecs(result, translations = {}) {
  const specs = [];
  const maxControlledPathsLabel =
    translations.maxControlledPaths || 'Max Controlled Paths';
  const controlledAxesPathLabel =
    translations.controlledAxesPath || 'Controlled Axes / Path';
  const spindleAxesPathLabel =
    translations.spindleAxesPath || 'Spindle Axes / Path';

  if (result.maxControlledPaths) {
    specs.push({
      icon: 'controlled-paths',
      label: maxControlledPathsLabel,
      value: result.maxControlledPaths,
      unit: '',
    });
  }

  if (result.controlledAxesPerPath) {
    specs.push({
      icon: 'controlled-axes',
      label: controlledAxesPathLabel,
      value: result.controlledAxesPerPath,
      unit: '',
    });
  }

  if (result.maxSpindleAxesTotalPerPath) {
    specs.push({
      icon: 'spindle-axes',
      label: spindleAxesPathLabel,
      value: result.maxSpindleAxesTotalPerPath,
      unit: '',
    });
  }

  return specs;
}

/**
 * Check if CNC has any specs to display
 * @param {Object} result - The search result
 * @returns {boolean}
 */
function hasSpecs(result) {
  return !!(
    result.maxControlledPaths ||
    result.controlledAxesPerPath ||
    result.maxSpindleAxesTotalPerPath
  );
}

/**
 * Format Algolia result to match product-card component structure
 *
 * This function remaps Algolia search result fields to match the structure expected by
 * the product-card component (_components/modules/product-card.twig).
 *
 * @param {Object} result - The raw Algolia search result object
 * @param {Object} translations - Translation labels for card attributes
 * @returns {Object} Formatted data matching product-card structure
 */
function formatCardData(result, translations) {
  return {
    heading: result.title || '',
    url: result.absoluteUri || result.url || '#',
    image: result.productImage || null,
    category:
      result.productSeries && result.productSeries.length
        ? result.productSeries[0]
        : null,
    description: result.description || result.tagline || null,
    attributes: getSpecs(result, translations).map((spec) => ({
      label: spec.label,
      value: spec.value + spec.unit,
      icon: spec.icon,
    })),
    legacy: result.legacy || false,
  };
}

// Create and export the Alpine component using the base factory

export default createProductSearchGrid({
  appId: cncsConfig.app_id,
  apiKey: cncsConfig.api_key,
  indexName: cncsConfig.index,
  configureWidget: createProductConfig('[data-search-cncs]'),
  formatCardData,
  getSpecs,
  hasSpecs,
  scrollToSelector: '[data-search-cncs]',
});
